import { useState } from "react";
import { fileUrl, fmtPrice } from "@/lib/api";
import { Heart, Plus, Star } from "lucide-react";
import { motion } from "framer-motion";

const FALLBACK = "/image-placeholder.svg";

export default function ProductCard({ product: p, shop, onOpen, onAdd, index = 0 }) {
  const [liked, setLiked] = useState(false);
  const hasDiscount = p.discount_percent > 0;
  const finalPrice = p.price * (1 - (p.discount_percent || 0) / 100);

  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.04, 0.28) }}
      data-testid={`product-card-${p.id}`}
      className="group min-w-0"
    >
      <div className="relative">
        <button
          onClick={onOpen}
          data-testid={`open-product-${p.id}`}
          className="relative block w-full overflow-hidden rounded-[24px] bg-secondary text-start shadow-card sm:rounded-[28px]"
        >
          <div className="aspect-[4/5] overflow-hidden">
            <img
              src={p.image_path ? fileUrl(p.image_path) : FALLBACK}
              alt={p.name}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
            />
          </div>
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-brand/40 to-transparent opacity-70" />
          {hasDiscount && (
            <span className="absolute bottom-3 right-3 rounded-full bg-primary px-2.5 py-1 text-[10px] font-extrabold text-primary-foreground shadow-cta sm:text-xs">
              خصم {p.discount_percent}%
            </span>
          )}
          {!p.in_stock && (
            <div className="absolute inset-0 grid place-items-center bg-brand/60 text-sm font-bold text-brand-foreground">نفدت الكمية</div>
          )}
        </button>

        <button
          type="button"
          aria-label={liked ? "إزالة من المفضلة" : "إضافة إلى المفضلة"}
          onClick={() => setLiked((value) => !value)}
          className={`absolute left-3 top-3 grid h-9 w-9 place-items-center rounded-full shadow-sm backdrop-blur-sm transition-all ${liked ? "bg-brand text-brand-foreground" : "bg-card/90 text-brand hover:bg-card"}`}
        >
          <Heart className={`h-4 w-4 ${liked ? "fill-current" : ""}`} />
        </button>
      </div>

      <div className="px-1 pb-1 pt-3">
        <div className="flex items-start justify-between gap-2">
          <button onClick={onOpen} className="min-w-0 flex-1 text-start">
            <h3 className="line-clamp-1 text-[13px] font-extrabold leading-5 sm:text-[15px]">{p.name}</h3>
            <p className="mt-0.5 line-clamp-1 text-[10px] text-muted-foreground sm:text-xs">
              {p.shop_name || p.category}
            </p>
          </button>
          <div className="mt-0.5 flex shrink-0 items-center gap-1 text-[10px] font-bold sm:text-xs">
            <Star className="h-3 w-3 fill-rating text-rating" />
            {p.rating > 0 ? p.rating : "جديد"}
          </div>
        </div>

        <p className="mt-2 hidden line-clamp-1 text-xs text-muted-foreground sm:block">{p.description || "منتج مختار بعناية"}</p>

        <div className="mt-2.5 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <span className="font-num block truncate text-sm font-extrabold text-foreground sm:text-base">{fmtPrice(finalPrice)}</span>
            {hasDiscount && <span className="block text-[10px] text-muted-foreground line-through">{fmtPrice(p.price)}</span>}
          </div>
          {onAdd && (
            <button
              data-testid={`add-to-cart-${p.id}`}
              disabled={p.in_stock === false || (shop && shop.is_open === false)}
              onClick={onAdd}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand text-brand-foreground shadow-sm transition-all hover:-translate-y-0.5 hover:bg-primary active:scale-90 disabled:cursor-not-allowed disabled:opacity-35 sm:h-10 sm:w-10"
            >
              <Plus className="h-[18px] w-[18px]" />
            </button>
          )}
        </div>
      </div>
    </motion.article>
  );
}
